import React, { useState, useEffect } from 'react';
import { Form, Card, Button, Spinner, Alert, Row, Col, InputGroup } from 'react-bootstrap';
import '../styles/grid.css';
import { NavSidebar } from './components/NavSidebar';
import { useAuth } from '../context/AuthContext';
import Company from "../models/Company";
import { useHistory } from "react-router-dom";
import { MdArrowBack, MdDelete, MdAdd } from 'react-icons/md';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CompanySettingsScreen = () => {
  const {
    user,
    isSuperAdmin
  } = useAuth();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companySettings, setCompanySettings] = useState({});
  const [newFieldName, setNewFieldName] = useState('');
  const [zones, setZones] = useState([]);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [isSavingZone, setIsSavingZone] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin()) {
      history.push('/');
      return;
    }
    fetchCompanies();
    fetchZones();
  }, [isSuperAdmin, history]);

  useEffect(() => {
    if (selectedCompany?.zone_id) {
      setSelectedZoneId(selectedCompany.zone_id);
    }
  }, [selectedCompany]);

  const fetchCompanies = async () => {
    setIsLoading(true);
    try {
      console.log('Fetching companies...');
      const allCompanies = await Company.all();
      console.log('Companies fetched:', allCompanies);
      setCompanies(allCompanies);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error(`Error fetching companies: ${error.message || error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchZones = async () => {
    try {
      console.log('Fetching zones...');
      const allZones = await Company.fetchZones();
      console.log('Zones fetched:', allZones);
      setZones(allZones);
    } catch (error) {
      console.error('Error fetching zones:', error);
      toast.error(`Error fetching timezones: ${error.message || error}`);
    }
  };

  const handleCompanySelect = async (companyUuid) => {
    if (!companyUuid) {
      setSelectedCompany(null);
      setCompanySettings({});
      return;
    }

    setIsLoading(true);
    try {
      console.log('Fetching company with UUID:', companyUuid);
      const company = await Company.findByUUID(companyUuid);
      console.log('Company fetched:', company);
      
      // Check if company is actually found (not empty array)
      if (!company || (Array.isArray(company) && company.length === 0)) {
        console.error('No company found for UUID:', companyUuid);
        setSelectedCompany(null);
        setCompanySettings({});
        return;
      }
      
      setSelectedCompany(company);
      
      // Parse flags JSON
      let flags = {};
      if (company.flags && company.flags !== "0" && company.flags !== 0) {
        try {
          // Handle Java-style HashMap format
          if (typeof company.flags === 'string' && company.flags.includes('=')) {
            // Convert Java HashMap string to JSON
            const javaMapRegex = /(\w+)=(\w+)/g;
            const jsonObj = {};
            let match;
            while ((match = javaMapRegex.exec(company.flags)) !== null) {
              jsonObj[match[1]] = match[2] === 'true' ? true : match[2] === 'false' ? false : match[2];
            }
            flags = jsonObj;
            console.log('Converted Java HashMap to JSON:', flags);
          } else {
            flags = typeof company.flags === 'string' ? JSON.parse(company.flags) : company.flags;
            console.log('Parsed flags:', flags);
          }
        } catch (e) {
          console.error('Error parsing company flags:', e);
          flags = {};
        }
      }
      
      // Set all flags as they are
      setCompanySettings(flags || {});
    } catch (error) {
      console.error('Error fetching company details:', error);
      toast.error(`Error fetching company details: ${error.message || error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSettingChange = (setting, value) => {
    setCompanySettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const handleAddField = () => {
    if (newFieldName.trim() && !companySettings.hasOwnProperty(newFieldName)) {
      setCompanySettings(prev => ({
        ...prev,
        [newFieldName]: false
      }));
      setNewFieldName('');
    }
  };

  const handleRemoveField = (fieldName) => {
    setCompanySettings(prev => {
      const newSettings = { ...prev };
      delete newSettings[fieldName];
      return newSettings;
    });
  };

  const handleSubmit = async () => {
    if (!selectedCompany) return;

    setIsLoading(true);
    try {
      // Update flags as JSON
      const jsonString = JSON.stringify(companySettings);
      console.log('Saving company settings as JSON:', jsonString);

      const updates = {
        uuid: selectedCompany.uuid,
        flags: jsonString,
        company_targets: selectedCompany.company_targets,
        data_assumptions: selectedCompany.data_assumptions
      };

      const result = await Company.updateSettings(updates);

      if (result && !result.errors) {
        toast.success('Company settings have been updated successfully!');
        // Don't refresh - keep the current state to show persistence
      } else {
        throw new Error(result?.errors?.[0]?.message || 'Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating company settings:', error);
      toast.error(`Error saving settings: ${error.message || error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleZoneChange = async (e) => {
    const newZoneId = parseInt(e.target.value);
    setSelectedZoneId(newZoneId);
    setIsSavingZone(true);

    try {
      const result = await Company.updateZone(selectedCompany.uuid, newZoneId);

      if (result?.data?.updateCompany) {
        // Update the selected company with new zone data
        const updatedCompany = {
          ...selectedCompany,
          zone_id: newZoneId,
          zone: result.data.updateCompany.zone
        };
        setSelectedCompany(updatedCompany);

        // Update in companies list
        setCompanies(prevCompanies =>
          prevCompanies.map(c =>
            c.uuid === selectedCompany.uuid ? updatedCompany : c
          )
        );

        toast.success(`Timezone updated to ${result.data.updateCompany.zone.name}`, {
          position: "bottom-right",
          autoClose: 3000
        });
      } else if (result?.errors) {
        throw new Error(result.errors[0]?.message || 'Failed to update timezone');
      }
    } catch (error) {
      console.error('Error updating timezone:', error);
      toast.error('Failed to update timezone. Please try again.', {
        position: "bottom-right",
        autoClose: 5000
      });
      // Revert to previous zone
      setSelectedZoneId(selectedCompany.zone_id);
    } finally {
      setIsSavingZone(false);
    }
  };

  return (
    <div className="d-flex flex-row justify-content-start align-items-start">
      <NavSidebar />
      <div className="d-flex flex-column rounded-lg bg-white p-3 mt-2"
        style={{ width: '87vw', minWidth: '800px' }}
      >
        <div className="d-flex align-items-center mb-3">
          <Button
            variant="link"
            onClick={() => history.push('/migration')}
            className="p-0 mr-3"
          >
            <MdArrowBack size={24} />
          </Button>
          <h2>Company Settings</h2>
        </div>

        {!isSuperAdmin() ? (
          <Alert variant="danger">
            You do not have permission to access this page.
          </Alert>
        ) : (
          <>
            <Card className="mb-4">
              <Card.Body>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Select Company</Form.Label>
                    <Form.Control
                      as="select"
                      value={selectedCompany?.uuid || ''}
                      onChange={(e) => handleCompanySelect(e.target.value)}
                      disabled={isLoading}
                    >
                      <option value="">-- Select a Company --</option>
                      {companies.map(company => (
                        <option key={company.uuid} value={company.uuid}>
                          {company.title || company.name}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Form>
              </Card.Body>
            </Card>

            {selectedCompany && (
              <Card>
                <Card.Body>
                  <h4 className="mb-4">Settings for {selectedCompany.title || selectedCompany.name}</h4>

                  <Form>
                    {zones.length > 0 && (
                      <Card className="mb-3">
                        <Card.Header>
                          <h5 className="mb-0">Company Timezone</h5>
                        </Card.Header>
                        <Card.Body>
                          <Form.Group className="mb-3">
                            <Form.Label>Default Timezone</Form.Label>
                            <Form.Control
                              as="select"
                              value={selectedZoneId || ''}
                              onChange={handleZoneChange}
                              disabled={isSavingZone}
                            >
                              {zones.map(zone => (
                                <option key={zone.id} value={zone.id}>
                                  {zone.name} ({zone.title}) - {zone.code}
                                </option>
                              ))}
                            </Form.Control>
                            <Form.Text className="text-muted">
                              This timezone will be used for all time-based operations for {selectedCompany.title || selectedCompany.name}.
                              Changes are saved automatically.
                            </Form.Text>
                          </Form.Group>

                          {selectedCompany.zone && (
                            <Alert variant="info" className="mb-0">
                              Current timezone: <strong>{selectedCompany.zone.name} ({selectedCompany.zone.title})</strong>
                            </Alert>
                          )}
                        </Card.Body>
                      </Card>
                    )}

                    <Card className="mb-3">
                      <Card.Header>
                        <h5 className="mb-0">Company Flags</h5>
                      </Card.Header>
                      <Card.Body>
                        {Object.entries(companySettings).map(([fieldName, fieldValue]) => (
                          <Form.Group key={fieldName} className="mb-3">
                            <Row className="align-items-center">
                              <Col xs={8}>
                                <Form.Check
                                  type="switch"
                                  id={fieldName}
                                  label={fieldName}
                                  checked={fieldValue === true}
                                  onChange={(e) => handleSettingChange(fieldName, e.target.checked)}
                                  disabled={isLoading}
                                />
                              </Col>
                              <Col xs={4} className="text-right">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleRemoveField(fieldName)}
                                  disabled={isLoading}
                                >
                                  <MdDelete /> Remove
                                </Button>
                              </Col>
                            </Row>
                          </Form.Group>
                        ))}

                        {Object.keys(companySettings).length === 0 && (
                          <p className="text-muted">No flags configured. Add a new flag below.</p>
                        )}

                        <hr className="my-4" />

                        <Form.Group>
                          <Form.Label>Add New Flag</Form.Label>
                          <InputGroup>
                            <Form.Control
                              type="text"
                              placeholder="Enter flag name (e.g., disableFavoriteFlags)"
                              value={newFieldName}
                              onChange={(e) => setNewFieldName(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddField();
                                }
                              }}
                              disabled={isLoading}
                            />
                            <InputGroup.Append>
                              <Button
                                variant="outline-primary"
                                onClick={handleAddField}
                                disabled={isLoading || !newFieldName.trim()}
                              >
                                <MdAdd /> Add Flag
                              </Button>
                            </InputGroup.Append>
                          </InputGroup>
                        </Form.Group>

                        <Button
                          variant="primary"
                          onClick={handleSubmit}
                          disabled={isLoading}
                          className="mt-3"
                        >
                          {isLoading ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="mr-2"
                              />
                              Saving...
                            </>
                          ) : (
                            'Save Company Flags'
                          )}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Form>
                </Card.Body>
              </Card>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default CompanySettingsScreen;